import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, forkJoin, map, Observable, of, switchMap, take } from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { WorkflowCrudService } from '@blaze-runtime/engine-feature';
import {
  CreateWorkflowRequest,
  EnvironmentName,
  TableInfo,
  TriggerInfo,
  TriggerStates,
  TriggerType,
  WorkflowId,
} from '@blaze-shared/api-contracts-domain';
import {
  FeatureFlag,
  FeatureFlagsFacade,
  ProjectContextFacade,
} from '@blaze-shared/app-state-data-access';
import { ActivatedTriggersMap } from '@blaze-shared/datasets-domain';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { DialogService } from '@blaze-shared/shared-feature';
import { MAIN_INPUT_NAME } from '@blaze-shared/workflow-domain';

@Injectable({ providedIn: 'root' })
export class BlazeTableTriggersService {
  private readonly dialogService = inject(DialogService);
  private readonly featureFlagsFacade = inject(FeatureFlagsFacade);
  private readonly notificationService = inject(NotificationService);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly store = inject(Store);
  private readonly workflowCrudService = inject(WorkflowCrudService);

  /**
   * For triggers in non-multiple environment app use `null` as environment name.
   */
  environmentName: EnvironmentName | null = null;
  multipleEnvironmentsEnabled = false;

  constructor() {
    combineLatest({
      environmentName: this.projectContextFacade.environmentName$,
      multipleEnvironmentsEnabled: this.featureFlagsFacade.isEnabled(
        FeatureFlag.MultipleEnvironments,
        false,
      ),
    }).subscribe(({ environmentName, multipleEnvironmentsEnabled }) => {
      this.multipleEnvironmentsEnabled = multipleEnvironmentsEnabled;

      if (this.multipleEnvironmentsEnabled && environmentName) {
        this.environmentName = environmentName;
      } else {
        this.environmentName = null;
      }
    });
  }

  createNewTrigger(table: TableInfo): Observable<TriggerInfo | null> {
    return this.store.select(runtimeSelectors.selectProjectId).pipe(
      take(1),
      switchMap((projectId) => {
        const wfCreateRequest: CreateWorkflowRequest = {
          inputs: {}, // Backend does not need it
          inputSchemas: [
            {
              id: '', // Backend does not need it
              name: '', // Backend does not need it
              inputName: MAIN_INPUT_NAME,
              columns: [],
              datasetId: table.id, // Takes precedence over columns
            },
          ],
          name: 'Triggered Workflow',
          projectId,
          pageId: null,
          isLongRunning: false,
          isServer: false,
          nodes: [],
          startNodeId: null,
          workflowType: 'Trigger',
        };
        return this.workflowCrudService.createWorkflow(wfCreateRequest).pipe(
          catchError((err) => {
            this.notificationService.showError(err);
            return of(null);
          }),
        );
      }),
      map((workflow) => {
        if (!workflow) {
          return null;
        }

        this.store.dispatch(runtimeActions.workflowAdd({ workflow }));

        const newTrigger: TriggerInfo = {
          workflowId: workflow.id,
          triggerType: 'Insert',
          inputData: 'NewValue',
          cycleDetected: false,
          states: this.createStates(),
        };

        return newTrigger;
      }),
    );
  }

  promptRemoveTrigger(isActive = false): Observable<boolean> {
    return this.dialogService
      .openConfirmDialog(
        {
          headerIcon: 'icon_trash_16',
          title: `Delete Trigger Action`,
          description: `This will remove the trigger${isActive ? ' and stop its workflow' : ''}.`,
          confirmLabel: 'Delete Trigger',
          cancelLabel: 'Cancel',
        },
        {
          panelClass: ['bd', 'bd-dialog', 'blaze-table-triggers-dialog'],
        },
      )
      .afterClosed()
      .pipe(map(Boolean));
  }

  /**
   * Triggered workflows are removed when a trigger is removed by the backend.
   * Notify the store that those workflows are not available anymore.
   * @param workflowId
   * @param hard - If true removes workflow only on frontend side from store
   */
  postRemoveWorkflow(workflowId: WorkflowId, hard = false): Observable<boolean> {
    if (!hard) {
      this.store.dispatch(runtimeActions.workflowsRemoved({ workflowIds: [workflowId] }));
      return of(true);
    }

    return this.workflowCrudService.deleteWorkflow(workflowId, false).pipe(map(() => true));
  }

  removePristineWorkflows(workflowIds: WorkflowId[]): Observable<WorkflowId[]> {
    const requests$ = workflowIds.map((workflowId) =>
      this.workflowCrudService.deleteWorkflow(workflowId, false).pipe(catchError(() => of())),
    );

    return forkJoin(requests$).pipe(
      map(() => {
        this.store.dispatch(runtimeActions.workflowsRemoved({ workflowIds }));

        return workflowIds;
      }),
    );
  }

  isTriggerActive(
    trigger: Partial<TriggerInfo> | undefined,
    environmentName = this.environmentName,
  ): boolean {
    return (
      trigger?.states?.find((state) => state.environment === environmentName)?.isActive ?? false
    );
  }

  canActivateTrigger(
    workflowId: WorkflowId | undefined,
    triggerType: TriggerType | undefined,
    activatedTriggers: ActivatedTriggersMap,
  ): boolean {
    if (!workflowId || !triggerType) {
      return false;
    }

    let activatedTriggerForType: WorkflowId | undefined;
    switch (triggerType) {
      case 'Insert':
        activatedTriggerForType = activatedTriggers?.Insert ?? activatedTriggers?.Upsert;
        break;
      case 'Update':
        activatedTriggerForType = activatedTriggers?.Update ?? activatedTriggers?.Upsert;
        break;
      case 'Upsert':
        activatedTriggerForType =
          activatedTriggers?.Insert ?? activatedTriggers?.Update ?? activatedTriggers?.Upsert;
        break;
      case 'Delete':
        activatedTriggerForType = activatedTriggers?.Delete;
        break;
    }

    return !activatedTriggerForType || activatedTriggerForType === workflowId;
  }

  setTriggerIsActiveMutably(isActive: boolean, trigger: TriggerInfo): void {
    if (!trigger.states) {
      return;
    }

    if (!this.multipleEnvironmentsEnabled) {
      trigger.states[0].isActive = isActive;
      return;
    }

    if (trigger.states.length !== 2) {
      throw new Error(`Invalid trigger states for multi environment`);
    }

    if (this.environmentName === 'Development') {
      trigger.states[0].isActive = isActive;
      return;
    }

    // Production
    trigger.states[1].isActive = isActive;
  }

  createStates(isActiveDevelopment = false, isActiveProduction = false): TriggerStates {
    if (!this.multipleEnvironmentsEnabled) {
      return [
        {
          environment: null,
          isActive: isActiveDevelopment,
        },
      ];
    }

    return [
      {
        environment: 'Development',
        isActive: isActiveDevelopment,
      },
      {
        environment: 'Production',
        isActive: isActiveProduction,
      },
    ];
  }
}
